import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'

import {BTN_PRIMARY} from '../constants'
import {useQuestionTimer} from '../hooks'
import type {Question, QuestionAnswerTiming} from '../types'
import {ProgressBar} from './ProgressBar'
import {TimerBadge} from './TimerBadge'

export const QuestionScreen = ({
  question,
  questionNumber,
  totalQuestions,
  onAnswerLocked,
  onAnswer,
}: {
  question: Question
  questionNumber: number
  totalQuestions: number
  onAnswerLocked: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
  onAnswer: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
}) => {
  const [selected, setSelected] = useState<string | null>(null)
  const [fermiInput, setFermiInput] = useState('')
  const [answered, setAnswered] = useState(false)
  const [earnedScore, setEarnedScore] = useState(0)
  const [isCorrect, setIsCorrect] = useState(false)
  const [answerTiming, setAnswerTiming] = useState<QuestionAnswerTiming | null>(null)
  const startedAtRef = useRef(Date.now())

  const getAnswerTiming = (timedOut: boolean, remainingSeconds: number): QuestionAnswerTiming => ({
    elapsedMs: Date.now() - startedAtRef.current,
    remainingSeconds,
    timedOut,
  })

  const handleTimerExpire = () => {
    if (answered) {
      return
    }

    setAnswered(true)
    setEarnedScore(0)
    setIsCorrect(false)
    setSelected(null)

    const timing = getAnswerTiming(true, 0)
    setAnswerTiming(timing)
    onAnswerLocked('', 0, false, timing)
  }

  const {remaining, total} = useQuestionTimer(question.timerSeconds, answered, handleTimerExpire)

  const isPhase1 = question.phase === 1
  const dim = isPhase1 && !answered ? (1 - remaining / total) * 0.72 : 0
  const contentRef = useRef<HTMLDivElement>(null)
  const questionFlashlightRef = useRef<HTMLDivElement>(null)
  const questionCursorRef = useRef<HTMLDivElement>(null)

  // Mouse tracking for flashlight (Phase 1 only)
  useEffect(() => {
    if (!isPhase1 || answered) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (questionFlashlightRef.current) {
        questionFlashlightRef.current.style.background = `radial-gradient(circle 120px at ${e.clientX}px ${e.clientY}px, rgba(255,255,255,0.14) 0%, transparent 100%)`
      }

      if (questionCursorRef.current) {
        questionCursorRef.current.style.left = `${e.clientX}px`
        questionCursorRef.current.style.top = `${e.clientY}px`
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.body.style.cursor = 'none'
    const style = document.createElement('style')
    style.textContent = '*, *:hover { cursor: none !important; }'
    document.head.appendChild(style)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.body.style.cursor = ''
      style.remove()
    }
  }, [isPhase1, answered])

  const handleSelect = (option: string) => {
    if (answered) {
      return
    }

    setSelected(option)
    const correct = option === question.correctAnswer
    const score = correct ? question.points : 0
    const timing = getAnswerTiming(false, remaining)

    setEarnedScore(score)
    setIsCorrect(correct)
    setAnswered(true)
    setAnswerTiming(timing)
    onAnswerLocked(option, score, correct, timing)
  }

  const handleFermiSubmit = () => {
    if (answered || !fermiInput) {
      return
    }

    const value = parseFloat(fermiInput)
    const [min, max] = question.fermiRange ?? [0, 0]
    let score = 0

    if (!isNaN(value)) {
      if (value >= min && value <= max) {
        score = question.points
      } else if (value >= min / 2 && value <= max * 2) {
        score = Math.floor(question.points / 2)
      }
    }

    const correct = score === question.points
    const timing = getAnswerTiming(false, remaining)

    setEarnedScore(score)
    setIsCorrect(correct)
    setAnswered(true)
    setSelected(fermiInput)
    setAnswerTiming(timing)
    onAnswerLocked(fermiInput, score, correct, timing)
  }

  const [exiting, setExiting] = useState(false)

  const handleNext = () => {
    const timing = answerTiming ?? getAnswerTiming(false, remaining)

    if (questionNumber >= totalQuestions) {
      setExiting(true)
      setTimeout(() => onAnswer(selected ?? '', earnedScore, isCorrect, timing), 500)
    } else {
      onAnswer(selected ?? '', earnedScore, isCorrect, timing)
    }
  }

  const isFermi = question.type === 'fermi'
  const buttonLabel = questionNumber < totalQuestions ? 'Next question' : 'See results'

  return (
    <div
      ref={contentRef}
      className="relative flex min-h-screen flex-col px-6 py-8 transition-opacity duration-500 ease-out md:mx-auto md:w-full md:max-w-3xl"
      style={{opacity: exiting ? 0 : 1}}
    >
      {isPhase1 && dim > 0 && (
        <div className="pointer-events-none fixed inset-0 z-10" style={{background: `rgba(0,0,0,${dim})`}} />
      )}

      {isPhase1 && !answered && <div ref={questionFlashlightRef} className="pointer-events-none fixed inset-0 z-20" />}

      {isPhase1 && !answered && (
        <div
          ref={questionCursorRef}
          className="pointer-events-none fixed z-50 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-moss-white"
          style={{left: '-999px'}}
        />
      )}

      <div className="relative z-30 mb-10">
        <ProgressBar current={questionNumber} total={totalQuestions} />
      </div>

      <div className="h-[520px] overflow-y-auto">
        <div className="mb-1 flex items-center gap-2.5">
          <span className="text-12 font-medium uppercase tracking-[.08em] text-moss-grey-400">Level 1</span>
          <span className="flex items-center gap-1.5 text-10 text-grey-300">
            <span className="size-1 shrink-0 rounded-full bg-grey-300" />
            {question.points} points for the right answer
          </span>
        </div>

        <p className="mb-10 text-18 font-medium leading-tight text-moss-white md:text-22">{question.question}</p>

        {isFermi && !answered && (
          <div className="mb-8 flex gap-3">
            <input
              type="number"
              min={0}
              value={fermiInput}
              onChange={(e) => setFermiInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFermiSubmit()}
              placeholder={question.fermiInputPlaceholder ?? 'Enter your estimate'}
              className="flex-1 rounded-lg border border-grey-300 bg-transparent px-4 py-3.5 text-14 text-moss-white caret-green-neon placeholder:text-moss-grey-500 focus:border-moss-white focus:outline-none"
            />
            <button onClick={handleFermiSubmit} disabled={!fermiInput} className={cn(BTN_PRIMARY, 'duration-[800ms]')}>
              Submit
            </button>
          </div>
        )}

        {isFermi && answered && (
          <div
            className={cn('mb-6 flex items-center justify-between rounded-lg border px-5 py-4', {
              'border-green-neon bg-green-neon/10': isCorrect,
              'border-ui-red-450 bg-ui-red-450/10': !isCorrect,
            })}
          >
            <div>
              <p className={cn('text-14', isCorrect ? 'text-green-neon' : 'text-ui-red-450')}>
                Your answer: <span className="font-medium">{selected}</span>
              </p>
              <p className="mt-0.5 text-12 text-moss-grey-400">
                Correct range: {question.fermiRange?.[0].toLocaleString()} – {question.fermiRange?.[1].toLocaleString()}
                {question.fermiRangeSuffix ? ` ${question.fermiRangeSuffix}` : ''}
              </p>
            </div>
            {isCorrect && (
              <span className="whitespace-nowrap text-14 font-semibold text-green-neon">+{earnedScore} PTS</span>
            )}
          </div>
        )}

        {!isFermi && !answered && (
          <div className="mb-8 grid gap-3">
            {question.options.map((option) => (
              <button
                key={option}
                onClick={() => handleSelect(option)}
                className="w-full rounded-lg border border-grey-300 bg-transparent px-5 py-4 text-left text-14 leading-snug text-grey-110 transition-colors hover:border-moss-white hover:text-moss-white"
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {!isFermi && answered && (
          <div
            className={cn('relative mb-6 flex items-center justify-between rounded-lg border px-5 py-4', {
              'border-green-neon bg-green-neon/10': isCorrect,
              'border-ui-red-450 bg-ui-red-450/10': !isCorrect,
            })}
          >
            <span className={cn('text-14', isCorrect ? 'text-green-neon' : 'text-ui-red-450')}>
              {selected ?? question.correctAnswer}
            </span>
            {isCorrect && (
              <span className="whitespace-nowrap text-14 font-semibold text-green-neon">+{earnedScore} PTS</span>
            )}
            <img
              src="/images/fraud-game/thumbtack.png"
              alt=""
              className="absolute -top-4 right-1 h-[32px] w-[30px] animate-[pin-in_180ms_cubic-bezier(0.34,1.8,0.64,1)_forwards]"
              style={{filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))', transform: 'rotate(-15deg)'}}
            />
          </div>
        )}

        {answered && (
          <div className="mb-8 rounded-lg border border-grey-900 p-5">
            <div className="mb-3 flex items-center gap-2">
              <div className="size-1.5 rounded-full bg-green-neon" />
              <span className="text-12 font-semibold uppercase tracking-[.1em] text-green-neon">Finance Insight</span>
            </div>
            <p className="text-[15px] leading-relaxed text-moss-grey-300">{question.mossInsight.body}</p>
          </div>
        )}
      </div>

      <div className="relative z-30 flex items-center justify-between pt-4">
        <TimerBadge remaining={remaining} total={total} />
        <button onClick={handleNext} disabled={!answered} className={BTN_PRIMARY}>
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}
